import { ReactElement, useMemo, useState, useEffect, lazy, Suspense, ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import 'assets/css/main.css';
import 'i18n';
import { languages } from 'i18n';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import URLS from 'URLS';
import { UserProvider } from 'hooks/User';
import { AdminProvider } from 'apps/AdminPanel/hooks/useAdmin';
import { NotificationsProvider } from 'hooks/Notifications';
import { SnackbarProvider, useSnackbar } from 'hooks/Snackbar';
import { SchoolUserProvider, useAuth, useIsSchoolAuth } from 'hooks/Auth';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import firebase, { collection, COLLECTIONS } from 'fb';
import { subYears, endOfToday } from 'date-fns';
import { User } from 'types';
import CookieNotification from 'components/CookieNotification';
import { useUserState } from 'hooks/User';
import { OnboardingProvider, useOnboardingRedirect } from 'hooks/Onboarding';
import { SongsProvider } from 'SongCaching';
import { PlaylistsProvider } from 'apps/Players/hooks/usePlaylists';
import { PlayerDemoSongsProvider } from 'containers/PlayerDemo/PlayerDemoSongs';
import Landing from 'containers/Landing';
import Navigation from 'components/navigation/Navigation';
// Theme
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme from 'theme';
import { NewsletterProvider } from 'hooks/Newsletter';
// Project components
const ChallengeApp = lazy(() => import('apps/Challenge'));
const EventsApp = lazy(() => import('apps/Events'));
const MainPlayerApp = lazy(() => import('apps/Players/MainPlayer'));
const ChoirPlayerApp = lazy(() => import('apps/Players/ChoirPlayer'));
const BoomwhackersPlayerApp = lazy(() => import('apps/Players/BoomwhackersPlayer'));
const UkulelePlayerApp = lazy(() => import('apps/Players/UkulelePlayer'));
const GuitarPlayerApp = lazy(() => import('apps/Players/GuitarPlayer'));
const WhitePianoPlayerApp = lazy(() => import('apps/Players/WhitePianoPlayer'));
const PianoNotationPlayerApp = lazy(() => import('apps/Players/PianoNotationPlayer'));
const BlackJazzPianoPlayerApp = lazy(() => import('apps/Players/BlackJazzPianoPlayer'));
const BassTabsPlayerApp = lazy(() => import('apps/Players/BassTabsPlayer'));
const OpenTuningPlayerApp = lazy(() => import('apps/Players/OpenTuningPlayer'));
const SingPlayerApp = lazy(() => import('apps/Players/SingPlayer'));
const ComposerApp = lazy(() => import('apps/Composer'));
const AdminPanelApp = lazy(() => import('apps/AdminPanel'));
const AcademyApp = lazy(() => import('apps/Academy'));
const Hub = lazy(() => import('containers/Hub'));
const EdTech = lazy(() => import('containers/Hub/components/EdTech'));
const ResourcesApp = lazy(() => import('apps/Resources'));
const Auth = lazy(() => import('containers/Auth'));
const Chats = lazy(() => import('containers/Chats'));
const NotFound = lazy(() => import('containers/NotFound'));
const Account = lazy(() => import('containers/Account'));
const Organization = lazy(() => import('containers/Organization'));
const Request = lazy(() => import('containers/Request'));
const Privacy = lazy(() => import('containers/Privacy'));
const MailPreferences = lazy(() => import('containers/MailPreferences'));
const Pricing = lazy(() => import('containers/Pricing'));
const About = lazy(() => import('containers/About'));
const References = lazy(() => import('containers/References'));
const PlayerDemo = lazy(() => import('containers/PlayerDemo'));
const ShowInterest = lazy(() => import('containers/SchoolOnboarding/ShowInterest'));
const CreateLicense = lazy(() => import('containers/SchoolOnboarding/CreateLicense'));

export type ProvidersProps = {
  children: ReactNode;
};

export const Providers = ({ children }: ProvidersProps) => {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 1000 * 60 * 5,
            refetchOnWindowFocus: false,
          },
        },
      }),
  );
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <UserProvider>
            <SchoolUserProvider>
              <AdminProvider>
                <SnackbarProvider>
                  <NotificationsProvider>
                    <OnboardingProvider>
                      <NewsletterProvider>
                        <SongsProvider>
                          <PlaylistsProvider>
                            <PlayerDemoSongsProvider>
                              <CssBaseline />
                              {children}
                            </PlayerDemoSongsProvider>
                          </PlaylistsProvider>
                        </SongsProvider>
                      </NewsletterProvider>
                    </OnboardingProvider>
                  </NotificationsProvider>
                </SnackbarProvider>
              </AdminProvider>
            </SchoolUserProvider>
          </UserProvider>
        </LocalizationProvider>
      </ThemeProvider>
      <ReactQueryDevtools />
    </QueryClientProvider>
  );
};

export type AuthRouteProps = {
  element?: ReactElement;
  children?: ReactNode;
  ageRestrict?: boolean;
  schoolRestrict?: boolean;
  nonSchoolRestrict?: boolean;
  studentRestrict?: boolean;
  authRequired?: boolean;
  schoolRedirectUrl?: string;
  showFeideSnackbar?: boolean;
};

export const AuthRoute = ({
  ageRestrict,
  children,
  element,
  schoolRestrict = false,
  nonSchoolRestrict = false,
  studentRestrict = false,
  authRequired = true,
  schoolRedirectUrl,
  showFeideSnackbar = true,
}: AuthRouteProps) => {
  const { t } = useTranslation(['common']);
  const location = useLocation();

  const { showSnackbar } = useSnackbar();
  const [auth, isAuthLoading] = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<firebase.firestore.DocumentSnapshot<User> | null>(null);

  useEffect(() => {
    let isSubscribed = true;
    if (!isAuthLoading) {
      if (auth) {
        collection<User>(COLLECTIONS.USERS)
          .doc(auth.uid)
          .get()
          .then((user) => {
            if (isSubscribed) {
              setUser(user.exists ? user : null);
              setIsLoading(false);
            }
          })
          .catch((error) => {
            // Without this the route renders its loading state forever on a failed read.
            // eslint-disable-next-line no-console
            console.error('Failed to read the current user document', error);
            if (isSubscribed) {
              setUser(null);
              setIsLoading(false);
            }
          });
      } else {
        setIsLoading(false);
      }
    }
    return () => {
      isSubscribed = false;
    };
  }, [auth, isAuthLoading]);
  const over13 = useMemo(() => (user && user.data()!.birthday !== undefined ? user.data()!.birthday!.toDate() < subYears(endOfToday(), 13) : false), [user]);
  const haveParent = useMemo(() => (user ? Boolean(user.data()!.parents?.length) : false), [user]);
  const [isSchoolUser, isSchoolUserLoading, schoolAuthDoc] = useIsSchoolAuth();
  const isSchoolStudent = schoolAuthDoc !== null ? schoolAuthDoc?.data().role === 'student' : false;

  let content: ReactNode;
  if (isLoading || isAuthLoading || isSchoolUserLoading) {
    content = <Navigation isLoading noFooter />;
  } else if ((!auth || !user) && authRequired) {
    content = <Navigate state={{ referrer: location.pathname }} to={URLS.SIGN_IN} />;
  } else if (
    (isSchoolUser && schoolRestrict) ||
    (isSchoolUser && isSchoolStudent && studentRestrict) ||
    (!isSchoolUser && !user?.data()?.subscription?.schoolBypass && nonSchoolRestrict)
  ) {
    if (showFeideSnackbar) {
      if (schoolRestrict || studentRestrict) {
        setTimeout(() => showSnackbar(t('AUTH.NOACCESS.SCHOOLRESTRICTED'), { severity: 'info', length: 15000 }), 1000);
      } else if (nonSchoolRestrict) {
        setTimeout(() => showSnackbar(t('AUTH.NOACCESS.NONSCHOOLRESTRICTED'), { severity: 'info', length: 15000 }), 1000);
      }
    }
    content = <Navigate to={schoolRedirectUrl || URLS.HUB} />;
  } else if (ageRestrict && !over13 && !haveParent && !isSchoolUser) {
    setTimeout(() => showSnackbar(t('AUTH.NOACCESS.NOPARENT'), { severity: 'info', length: 15000 }), 1000);
    content = <Navigate to={URLS.HUB} />;
  } else {
    content = <>{element || children}</>;
  }

  return <>{content}</>;
};

export type LanguageCheckProps = {
  children: ReactNode;
};

const LanguageCheck = ({ children }: LanguageCheckProps) => {
  const user = useUserState();
  const { i18n } = useTranslation();

  useEffect(() => {
    if (user) {
      const userData = user.data();
      const currentLanguage = i18n.language;
      // if languageCode exist
      if (userData?.languageCode) {
        // if languageCode exist in valid languages
        if (languages.some((lang) => lang.code === userData.languageCode)) {
          // if local language is different from fb language
          if (currentLanguage !== userData.languageCode) {
            // change local language
            i18n.changeLanguage(userData?.languageCode);
          }
        }
      }
      // Change html lang on initial loading
      document.documentElement.setAttribute('lang', currentLanguage);
    }
  }, [user?.id]);
  return <div>{children}</div>;
};

const OnboardingRedirect = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { url, navigateNow, setNavigateNow, setAsDialog } = useOnboardingRedirect();

  useEffect(() => {
    if (navigateNow && url === location.pathname) {
      setAsDialog(false);
      setNavigateNow(false);
    } else if (url && navigateNow) {
      navigate(url);
      setNavigateNow(false);
    }
  }, [url, navigateNow]);

  return <>{children}</>;
};

// BrowserRouter wraps Providers, not the other way round: SnackbarProvider renders the snackbar itself, and a pushed
// notification's "Open" button in it is a router <Link>, which throws outside a router and unmounts the whole app.
const App = () => {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <Providers>
          <Helmet>
            <link href='/manifest.json' rel='manifest' />
          </Helmet>
          <Suspense fallback={null}>
            <LanguageCheck>
              <OnboardingRedirect>
                <Routes>
                  <Route element={<Auth />} path={`${URLS.SIGN_IN}*`} />
                  <Route
                    element={
                      <AuthRoute authRequired={false} studentRestrict={true}>
                        <Landing />
                      </AuthRoute>
                    }
                    path={URLS.LANDING}
                  />
                  <Route
                    element={
                      <AuthRoute authRequired={false} studentRestrict={true}>
                        <Pricing />
                      </AuthRoute>
                    }
                    path={URLS.PRICING}
                  />
                  <Route
                    element={
                      <AuthRoute authRequired={false} studentRestrict={true}>
                        <About />
                      </AuthRoute>
                    }
                    path={URLS.ABOUT}
                  />
                  <Route
                    element={
                      <AuthRoute authRequired={false} studentRestrict={true}>
                        <References />
                      </AuthRoute>
                    }
                    path={URLS.REFERENCES}
                  />
                  <Route element={<Hub />} path={URLS.HUB} />
                  <Route element={<EdTech />} path={URLS.EDTECH} />
                  <Route element={<Organization />} path={`${URLS.ORGANIZATION}:id/`} />
                  <Route element={<Privacy />} path={`${URLS.PRIVACY}`} />
                  <Route element={<Navigate replace to={URLS.SCHOOLONBOARDING_INTEREST} />} path={URLS.SCHOOLONBOARDING} />
                  <Route
                    element={
                      <AuthRoute authRequired={false} studentRestrict={true}>
                        <ShowInterest />
                      </AuthRoute>
                    }
                    path={`${URLS.SCHOOLONBOARDING_INTEREST}`}
                  />
                  <Route
                    element={
                      <AuthRoute authRequired={false} studentRestrict={true}>
                        <CreateLicense />
                      </AuthRoute>
                    }
                    path={`${URLS.SCHOOLONBOARDING_LICENSE}`}
                  />
                  <Route
                    element={
                      <AuthRoute schoolRestrict={true}>
                        <Chats />
                      </AuthRoute>
                    }
                    path={`${URLS.CHAT}*`}
                  />
                  <Route
                    element={
                      <AuthRoute>
                        <Request />
                      </AuthRoute>
                    }
                    path={`${URLS.REQUEST}:id/`}
                  />
                  <Route
                    element={
                      <AuthRoute>
                        <Account />
                      </AuthRoute>
                    }
                    path={URLS.ACCOUNT}
                  />
                  <Route
                    element={
                      <AuthRoute>
                        <Account />
                      </AuthRoute>
                    }
                    path={`${URLS.ACCOUNT}:params/`}
                  />
                  <Route
                    element={
                      <AuthRoute>
                        <MailPreferences />
                      </AuthRoute>
                    }
                    path={URLS.EMAIL_SUBSCRIPTIONS}
                  />
                  {import.meta.env.VITE_SHOW_CHALLENGE === 'TRUE' && (
                    <Route
                      element={
                        <AuthRoute authRequired={false} schoolRestrict={true}>
                          <ChallengeApp />
                        </AuthRoute>
                      }
                      path={`${URLS.CHALLENGE}*`}
                    />
                  )}
                  {import.meta.env.VITE_SHOW_EVENTS === 'TRUE' && (
                    <Route
                      element={
                        <AuthRoute schoolRestrict={true}>
                          <EventsApp />
                        </AuthRoute>
                      }
                      path={`${URLS.EVENTS}*`}
                    />
                  )}
                  {import.meta.env.VITE_SHOW_PLAYER === 'TRUE' && <Route element={<MainPlayerApp />} path={`${URLS.PLAYER}*`} />}
                  {import.meta.env.VITE_SHOW_CHOIRPLAYER === 'TRUE' && <Route element={<ChoirPlayerApp />} path={`${URLS.CHOIRPLAYER}*`} />}
                  {import.meta.env.VITE_SHOW_BOOMWHACKERSPLAYER === 'TRUE' && (
                    <Route
                      element={
                        <AuthRoute nonSchoolRestrict={true} schoolRedirectUrl={URLS.EDTECH}>
                          <BoomwhackersPlayerApp />
                        </AuthRoute>
                      }
                      path={`${URLS.BOOMWHACKERSPLAYER}*`}
                    />
                  )}
                  {import.meta.env.VITE_SHOW_UKULELEPLAYER === 'TRUE' && <Route element={<UkulelePlayerApp />} path={`${URLS.UKULELEPLAYER}*`} />}
                  {import.meta.env.VITE_SHOW_GUITARPLAYER === 'TRUE' && <Route element={<GuitarPlayerApp />} path={`${URLS.GUITARPLAYER}*`} />}
                  {import.meta.env.VITE_SHOW_WHITEPIANOPLAYER === 'TRUE' && <Route element={<WhitePianoPlayerApp />} path={`${URLS.WHITEPIANOPLAYER}*`} />}
                  {import.meta.env.VITE_SHOW_PIANONOTATIONPLAYER === 'TRUE' && (
                    <Route element={<PianoNotationPlayerApp />} path={`${URLS.PIANONOTATIONPLAYER}*`} />
                  )}
                  {import.meta.env.VITE_SHOW_BLACKJAZZPIANOPLAYER === 'TRUE' && (
                    <Route element={<BlackJazzPianoPlayerApp />} path={`${URLS.BLACKJAZZPIANOPLAYER}*`} />
                  )}
                  {import.meta.env.VITE_SHOW_BASSTABSPLAYER === 'TRUE' && <Route element={<BassTabsPlayerApp />} path={`${URLS.BASSTABSPLAYER}*`} />}
                  {import.meta.env.VITE_SHOW_OPENTUNINGPLAYER === 'TRUE' && <Route element={<OpenTuningPlayerApp />} path={`${URLS.OPENTUNINGPLAYER}*`} />}
                  {import.meta.env.VITE_SHOW_SINGPLAYER === 'TRUE' && <Route element={<SingPlayerApp />} path={`${URLS.SINGPLAYER}*`} />}
                  {import.meta.env.VITE_SHOW_COMPOSER === 'TRUE' && <Route element={<ComposerApp />} path={`${URLS.COMPOSER}*`} />}
                  {import.meta.env.VITE_SHOW_ADMINPANEL === 'TRUE' && (
                    <Route
                      element={
                        <AuthRoute authRequired={false} schoolRestrict={true}>
                          <AdminPanelApp />
                        </AuthRoute>
                      }
                      path={`${URLS.ADMINPANEL}*`}
                    />
                  )}
                  {import.meta.env.VITE_SHOW_ACADEMY === 'TRUE' && <Route element={<AcademyApp />} path={`${URLS.ACADEMY}*`} />}
                  {import.meta.env.VITE_SHOW_RESOURCES === 'TRUE' && <Route element={<ResourcesApp />} path={`${URLS.RESOURCES}*`} />}
                  <Route element={<PlayerDemo />} path={`${URLS.PLAYER_DEMO}:app`} />
                  <Route element={<NotFound />} path='*' />
                </Routes>
              </OnboardingRedirect>
            </LanguageCheck>
          </Suspense>
          <CookieNotification />
        </Providers>
      </BrowserRouter>
    </HelmetProvider>
  );
};

const container = document.getElementById('root')!;
const root = createRoot(container);
root.render(<App />);
